import { parseFrontmatter } from './parse-frontmatter'
import type { ChangelogAudience, ChangelogEntry } from './types'

const REQUIRED_FIELDS = [
    'version',
    'date',
    'audience',
    'category',
    'title',
] as const
const VALID_AUDIENCES: ChangelogAudience[] = ['all', 'internal']

function fileNameToId(path: string): string {
    const base = path.split('/').pop() ?? path
    return base.replace(/\.md$/, '')
}

function toEntry(path: string, raw: string): ChangelogEntry {
    const { data, body } = parseFrontmatter(raw)

    for (const field of REQUIRED_FIELDS) {
        if (!data[field]) {
            throw new Error(
                `Changelog entry "${path}" is missing required frontmatter field "${field}".`,
            )
        }
    }

    if (!VALID_AUDIENCES.includes(data.audience as ChangelogAudience)) {
        throw new Error(
            `Changelog entry "${path}" has invalid audience "${data.audience}" (expected one of: ${VALID_AUDIENCES.join(', ')}).`,
        )
    }

    // Sorting and last-seen comparison are lexicographic, so the date must be
    // zero-padded ISO; a loose value (e.g. 2026-6-1) would mis-sort silently.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(data.date)) {
        throw new Error(
            `Changelog entry "${path}" has invalid date "${data.date}" (expected YYYY-MM-DD).`,
        )
    }

    return {
        id: fileNameToId(path),
        version: data.version,
        date: data.date,
        audience: data.audience as ChangelogAudience,
        category: data.category,
        title: data.title,
        body,
    }
}

/**
 * Pure transform from a `{ path: rawMarkdown }` map (e.g. the result of Vite's
 * `import.meta.glob(..., { query: '?raw', eager: true })`) into validated,
 * date-descending changelog entries. Throws on malformed entries so authoring
 * mistakes fail fast at build/test time rather than rendering blank.
 */
export function buildEntries(
    modules: Record<string, string>,
): ChangelogEntry[] {
    return Object.entries(modules)
        .map(([path, raw]) => toEntry(path, raw))
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
}

const rawModules = import.meta.glob(
    ['../../content/changelog/*.md', '!../../content/changelog/README.md'],
    {
        query: '?raw',
        import: 'default',
        eager: true,
    },
)

const allEntries = buildEntries(rawModules as Record<string, string>)

/** Every changelog entry (all audiences), date-descending. */
export function loadEntries(): ChangelogEntry[] {
    return allEntries
}

/** Entries visible to regular users (excludes `internal`). */
export function getCustomerEntries(
    entries: ChangelogEntry[] = allEntries,
): ChangelogEntry[] {
    return entries.filter((e) => e.audience !== 'internal')
}

/** Every entry, including `internal`. For admin/staff views. */
export function getAllEntries(
    entries: ChangelogEntry[] = allEntries,
): ChangelogEntry[] {
    return entries
}

/**
 * The most recent date among customer-facing entries, or null when there are
 * none. Used to decide whether the "What's New" badge/modal should fire.
 */
export function getLatestEntryDate(entries: ChangelogEntry[]): string | null {
    const customer = getCustomerEntries(entries)
    if (customer.length === 0) return null
    return customer.reduce(
        (latest, e) => (e.date > latest ? e.date : latest),
        customer[0].date,
    )
}
